import {
  Alert,
  Divider,
  Heading,
  IconArrowBigRight,
  IconInfo,
  IconLink,
  LogoAmazonPay,
  LogoApplePay,
  LogoGooglePay,
  LogoKlarna,
  LogoMaestro,
  LogoMastercard,
  LogoPayPal,
  LogoVisa,
  Text,
  Tooltip,
} from "@idg-web-platform/pulse";
import css from "./orderDetailsPage.module.css";
import classNames from "classnames";
import { StatusBadge, StatusKind } from "./statusBadge.tsx";
import margins from "@idg-web-platform/pulse/cssModules/margins.module.css";
import { formatAmount, formatDate } from "../utils/formats.ts";
import { NavigationWrapper } from "../components/navigation/navigationWrapper.tsx";
import { useEffect } from "preact/hooks";
import { createContext } from "preact";
import { analyticsContentViewed } from "../utils/analyticsContentViewed.ts";
import { Order } from "./entities/Order.ts";
import { renderLineItem } from "./components/renderLineItem.tsx";
import { ShipmentDetail, ShipmentStepper, statusLabel } from "./components/shipmentDetail.tsx";
import analytics from "@idg-data-analytics-platform/analytics";
import { InvoiceState } from "./entities/InvoiceData.ts";

OrderDetailsPage.tagName = "control-order-details-page";

function renderPaymentMethod(paymentMethod: string) {
  const uc = paymentMethod.toUpperCase();
  if (uc.includes("PAYPAL")) return <LogoPayPal />;
  else if (uc.includes("MASTERCARD")) return <LogoMastercard />;
  else if (uc.includes("MAESTRO")) return <LogoMaestro />;
  else if (uc.includes("VISA")) return <LogoVisa />;
  else if (uc.includes("KLARNA")) return <LogoKlarna />;
  else if (uc.includes("AMAZON")) return <LogoAmazonPay />;
  else if (uc.includes("GOOGLE")) return <LogoGooglePay />;
  else if (uc.includes("APPLE")) return <LogoApplePay />;
  else return <span>{paymentMethod}</span>;
}

export const AppUrl = createContext("");

export function OrderDetailsPage({ order, appUrl }: { order: Order; appUrl: string }) {
  const isProcessed = order.cancelled.length > 0 || order.shipments.length > 0;

  const trackInvoiceDownloadClicked = () => {
    analytics({
      event: "generic_invoice_download_clicked",
      team: "control",
    });
  };

  useEffect(() => {
    analyticsContentViewed("order_details");
  }, []);

  return (
    <>
      <AppUrl.Provider value={appUrl}>
        <NavigationWrapper currentPath="/c/orders">
          <div className={css.root}>
            <div className={css.header}>
              <div className={css.headerStart}>
                <div data-hj-suppress>
                  <Heading
                    class={css.headerHeadline}
                    sizeStep={2}
                    removeMargin={true}
                    element="h1"
                    text={`Bestellung vom ${formatDate(order.date)}`}
                  />
                  <span className={css.label}>Bestellnummer:</span>
                  <span className={css.text}>{order.id}</span>
                </div>
              </div>

              <div className={css.split}>
                <div>
                  <p className={css.label}>Lieferadresse:</p>
                  <div>
                    {order.deliveryAddress.map((line) => (
                      <p data-dd-privacy="mask" data-hj-suppress className={css.text}>
                        {line}
                      </p>
                    ))}
                  </div>
                </div>
                <div>
                  <p className={css.label}>Rechnungsadresse:</p>
                  {order.invoiceAddress.map((line) => (
                    <p data-dd-privacy="mask" data-hj-suppress className={css.text}>
                      {line}
                    </p>
                  ))}
                </div>
              </div>
            </div>

            {order.shipments.length > 1 && (
              <Alert
                class={margins.pulseMarginBottomM}
                type="info"
                headline="Deine Pakete"
                description="Du erhältst deine Artikel in mehreren Paketen. Grund dafür sind die Entfernung, Verfügbarkeit und Preis - wir wählen immer die für dich beste Variante aus."
                showCloseButton={false}
              />
            )}

            {order.cancelled.length > 0 && (
              <div className={classNames(css.cancelled, margins.pulseMarginBottomL)}>
                <div className={css.sectionHeader}>
                  <Tooltip text="Manchmal kommt es vor, dass wir auf Grund von Bestandsdifferenzen einen Artikel stornieren müssen. Danke für dein Verständnis.">
                    <IconInfo />
                  </Tooltip>
                  <StatusBadge status={{ kind: StatusKind.Cancelled }} />
                </div>

                {order.cancelled.map((line) => renderLineItem(line, false))}
              </div>
            )}

            {/* If there are cancelled items or items dispatched to shipments, the received items are considered
                    processed and get an own section. */}

            {isProcessed && order.received.length > 0 && (
              <div className={classNames(css.shipment, margins.pulseMarginBottomL)}>
                <div className={css.shipmentHeader}>
                  <span />
                  <ShipmentStepper kind={StatusKind.Received} />
                </div>
                <div className={css.shipmentInfoBand}>
                  <div className={css.shipmentInfoStatus}>
                    <span className={css.statusLabel}>{statusLabel(StatusKind.Received)}</span>
                    <span className={css.statusDate}>am {formatDate(order.date)}</span>
                  </div>
                </div>

                {order.received.map((item) => renderLineItem(item))}
              </div>
            )}

            {/* Otherwise they are just displayed inline without a box. */}

            {!isProcessed && order.received.map((item) => renderLineItem(item))}

            {order.shipments.map((shipment, index) => (
              <ShipmentDetail
                shipment={shipment}
                index={index}
                shipmentsLength={order.shipments.length}
              />
            ))}

            <div className={classNames(css.split, css.result)}>
              <div>
                <div className={css.paymentMethod}>
                  <span className={css.label}>Zahlungsart:</span>
                  {renderPaymentMethod(order.paymentMethod)}
                </div>
                <Divider level={"primary"} className={css.divider} />
              </div>

              <div>
                <div className={css.totals}>
                  <Text fontStyle={"bold"} removeMargin={true}>
                    Zwischensumme
                  </Text>
                  <Text class={css.totalsAmount} fontStyle={"bold"} removeMargin={true}>
                    {formatAmount(order.itemsTotal)}
                  </Text>
                  {order.discount != 0.0 && (
                    <>
                      <Text sizeStep={-1} removeMargin={true} class={css.discount}>
                        Rabatt
                      </Text>
                      <Text
                        class={classNames(css.totalsAmount, css.discount)}
                        sizeStep={-1}
                        removeMargin={true}
                      >
                        {formatAmount(order.discount)}
                      </Text>
                    </>
                  )}
                  <Text sizeStep={-1} removeMargin={true}>
                    Zzgl. Versandkosten
                  </Text>
                  <Text class={css.totalsAmount} sizeStep={-1} removeMargin={true}>
                    {formatAmount(order.shippingCosts)}
                  </Text>
                  <Text sizeStep={1} fontStyle={"bold"} removeMargin={true}>
                    Gesamt{" "}
                    <Text element={"span"} sizeStep={-1} removeMargin={true}>
                      (inkl. MwSt.)
                    </Text>
                  </Text>
                  <Text
                    class={css.totalsAmount}
                    sizeStep={1}
                    fontStyle={"bold"}
                    removeMargin={true}
                  >
                    {formatAmount(order.total)}
                  </Text>
                </div>
                <div>
                  <Divider level={"primary"} className={css.divider} />
                </div>
              </div>
            </div>

            {order.invoiceData && (
              <div>
                <Text fontStyle={"bold"} removeMargin={false}>
                  Rechnung zu Bestellung:
                </Text>
                {order.invoiceData.invoiceState === InvoiceState.AVAILABLE ? (
                  <IconLink
                    text="Herunterladen"
                    href={order.invoiceData.invoiceUrl ?? ""}
                    iconComponent={<IconArrowBigRight />}
                    iconPosition="right"
                    onClick={trackInvoiceDownloadClicked}
                    // target/rel/name aren't in pulse IconLink's prop type but are forwarded to the <a> at runtime.
                    {...{ target: "_blank", rel: "noopener noreferrer", name: "Herunterladen" }}
                  />
                ) : (
                  <span>
                    Die Rechnung ist derzeit nicht verfügbar. Bitte versuche es später erneut oder
                    kontaktiere den <a href={"/c/account/contact?issue=payment"}>Kundenservice</a>.
                  </span>
                )}
              </div>
            )}
          </div>
        </NavigationWrapper>
      </AppUrl.Provider>
    </>
  );
}
